import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { getToken, setToken } from '@/api/client'
import * as ls from '@/api/ls'
import { takeFragmentToken } from '@/lib/loginFragment'

/**
 * 後台角色（批93-C 起共四個）。
 *
 * ★★ **`reseller` 刻意不進 `ROLE_RANK`**——它不是 admin 的子集（可建申請不可開通、
 * 只看自家客戶、限產品範圖），塞進線性階序會讓「rank ≥ operator」那類判斷把代理商
 * 一起放行。`rankOf('reseller') === 0` ⇒ 任何 rank 比較都 fail-closed，可見性一律
 * 走 `meta.roles` 精確比對（`router/index.ts`）與後端獨立 sub-router。
 *
 * 與後端 `app/core/security.py` 的 `ROLE_RANK`／`EXTERNAL_ROLES` **同一份定義**，
 * 改一邊要改兩邊。
 */
export type RoleName = 'operator' | 'admin' | 'manager' | 'superadmin' | 'reseller'

/**
 * 我方員工**四級**（批110 插入 `manager`）；**不含 reseller**（見上）。
 * 未知／未載入的角色一律 0（fail-closed）。
 *
 * ★★ `superadmin` 的值從 3 變成 **4**——所以任何地方都不准硬編 rank 數字，一律具名比較
 *    （`lib/jobs.ts` 的 `canRunJobKind` 就是批110 修掉的那個反例：寫死 `>= 3` 會讓營運主管
 *    在 UI 看到「回收站台」而後端擋下來）。
 */
export const ROLE_RANK: Record<string, number> = {
  operator: 1,
  admin: 2,
  manager: 3,
  superadmin: 4,
}

/**
 * 我方員工角色集合（＝`ROLE_RANK` 的鍵）；`isStaff` 與 router 的預設閘都用它。
 * ⚠️ 這是**字面量陣列不是從 `ROLE_RANK` 推導**——加角色時兩處都要改（後端那份是推導的，
 * 只有前端要手動同步；批110 差點漏掉這一行，漏了的話 manager 會被六條代管路由擋在門外）。
 */
export const STAFF_ROLES: readonly RoleName[] = ['operator', 'admin', 'manager', 'superadmin']

export function rankOf(role: string | null | undefined): number {
  return ROLE_RANK[role ?? ''] ?? 0
}

export const useAuthStore = defineStore('auth', () => {
  const token = ref<string | null>(getToken())
  const role = ref<string | null>(null)
  /** 自己的 email——申請詳情用來標示「這筆是你自己送的」（批108 起只是提示，不是閘）。 */
  const email = ref<string | null>(null)
  /** 代理商帳號所屬的公司；我方員工恆為 null。 */
  const resellerId = ref<string | null>(null)
  const resellerName = ref<string | null>(null)
  /**
   * 這個帳號的產品範圖（`products.key` 陣列）；**null＝不限**。
   * 只是可用性提示——真正的授權閘在後端每一支寫端點（`assert_product_in_scope`）。
   */
  const productKeys = ref<string[] | null>(null)
  /**
   * 批99-A：這個帳號看不看得到**代管中台**（導覽群、六條路由、全部 `/hosts` 系端點）。
   *
   * 權威在後端 `GET /api/admin/me` 的 `hosting_visible`＝「全系統支援代管的產品 ∩
   * 有效範圖 ≠ ∅」。★ 前端**不推導**（不要拿 `productKeys` 自己去算）——「哪些產品開放
   * 代管」是 `products.options.supports_hosting`，那份資料前端根本沒有完整拿到。
   *
   * `=== true` 是刻意的**嚴格比對**：舊後端不回這一欄 → undefined → false ＝ fail-closed。
   * 與 `productKeys` 的「缺席＝不限」相反，理由同 `api/ls.ts::getMe` 的註解。
   */
  const hostingVisible = ref(false)
  /**
   * 具體**開得了站**的產品鍵（已是「支援代管 ∩ 有效範圖」的交集，後端算好）。
   * 恆為陣列；空陣列＝一個都開不了（此時 `hostingVisible` 必然也是 false）。
   * 開站精靈與代管申請核可框只用它決定「這張證要簽哪個產品」。
   */
  const hostingProductKeys = ref<string[]>([])

  const isAuthenticated = computed(() => !!token.value)
  const roleRank = computed(() => rankOf(role.value))
  const isAdminOrAbove = computed(() => roleRank.value >= ROLE_RANK.admin)
  /**
   * 營運主管以上（批110）——**營運面**的可見性判準：產品憑證設計、兌換活動、代理商公司、
   * 客戶刪除／合併、稽核紀錄、代理商窗口帳號。
   * 與 `isSuperadmin` 的分工：那個留給「管人」（我方員工帳號與角色授予）與回收站台。
   */
  const isManagerOrAbove = computed(() => roleRank.value >= ROLE_RANK.manager)
  const isSuperadmin = computed(() => roleRank.value >= ROLE_RANK.superadmin)
  /** 我方員工（operator／admin／superadmin）——代理商一律 false。 */
  const isStaff = computed(() => roleRank.value >= ROLE_RANK.operator)
  /** 外部代理商帳號：只看得到 `/reseller/*` 三頁，其餘路由與端點全部 403。 */
  const isReseller = computed(() => role.value === 'reseller')

  function adoptToken(t: string) {
    setToken(t)
    token.value = t
  }

  /** 登入後載入自己的角色與範圖，決定可見的頁面與落地頁。 */
  async function loadMe() {
    if (!token.value) return
    try {
      const me = await ls.getMe()
      role.value = me.role
      email.value = me.email
      resellerId.value = me.reseller_id ?? null
      resellerName.value = me.reseller_name ?? null
      // `product_keys` 缺席（舊版後端）與 null 都當「不限」。
      productKeys.value = me.product_keys ?? null
      // 代管可見性相反：缺席＝看不到（fail-closed，見上方 ref 的註解）。
      hostingVisible.value = me.hosting_visible === true
      hostingProductKeys.value = me.hosting_product_keys ?? []
    } catch {
      role.value = null
      email.value = null
      resellerId.value = null
      resellerName.value = null
      productKeys.value = null
      hostingVisible.value = false
      hostingProductKeys.value = []
    }
  }

  function logout() {
    setToken(null)
    token.value = null
    role.value = null
    email.value = null
    resellerId.value = null
    resellerName.value = null
    productKeys.value = null
    hostingVisible.value = false
    hostingProductKeys.value = []
    if (location.hash) history.replaceState(null, '', location.pathname + location.search)
  }

  /**
   * 從 OAuth callback 的 URL fragment 取 token（#access_token=...）。main.ts 已先處理一次，此為保險。
   * 批185（FE-07）：與 `boot.ts` 同一套收件規則（`lib/loginFragment.ts`）——nonce 對不上就不收。
   */
  function adoptFromHashFragment(): boolean {
    const frag = takeFragmentToken(location.hash, 'access_token', { hasToken: !!getToken() })
    if (!frag.present) return false
    history.replaceState(null, '', location.pathname + location.search)
    if (!frag.token) return false
    adoptToken(frag.token)
    return true
  }

  return {
    token,
    role,
    email,
    resellerId,
    resellerName,
    productKeys,
    hostingVisible,
    hostingProductKeys,
    isAuthenticated,
    roleRank,
    isAdminOrAbove,
    isManagerOrAbove,
    isSuperadmin,
    isStaff,
    isReseller,
    adoptToken,
    loadMe,
    logout,
    adoptFromHashFragment,
  }
})
